import { OverlapBlock, ElementPosition } from "types/injector";

/**
 * Returns the overlap block that this current index appears in, or null
 * @param current The index to check
 * @param blocks The overlap blocks that this index might be in
 */
export const inOverlapBlock = (
  current: number,
  blocks: OverlapBlock[]
): OverlapBlock | null => {
  // The overlap blocks are a map of elements that overlap because
  // of CSS floats. It describes the Y position where those elements start
  // and end. If the current element falls between the start and end, it is
  // affected by the float and we probably shouldn't inject anything there.
  for (const block of blocks) {
    if (current >= block.start && current <= block.end) {
      return block;
    }
  }
  return null;
};

/**
 * Find the start of the overlap block
 */
const findStart = (current: number, positions: ElementPosition[]): number => {
  // higher = higher on the page. higher = a smaller Y coordinate
  let highestYOfBlock = positions[current].top;
  let start = current;

  // travel upwards through the elements
  for (let i = current; i > -1; i--) {
    // the bottom of the element is within the existing top of the block, so it's within the block
    if (positions[i].bottom > highestYOfBlock) {
      start = i;
      // the top of this element is higher than the current known top of block
      if (positions[i].top < highestYOfBlock) {
        highestYOfBlock = positions[i].top;
      }
    }
    // the bottom of the element is above the overlap block, we are free.
    else {
      break;
    }
  }
  return start;
};

/**
 * Find the end of the overlap block
 */
const findEnd = (current: number, positions: ElementPosition[]): number => {
  // lower = lower on the page. lower = a larger Y coordinate
  let lowestYOfBlock = positions[current].bottom;
  let end = current;

  // travel downwards through the elements
  for (let i = current; i < positions.length; i++) {
    // the top of the element is within the existing block, so it's within the block
    if (positions[i].top < lowestYOfBlock) {
      end = i;
      // the botom of this element is lower than the current known bottom of block
      if (positions[i].bottom > lowestYOfBlock) {
        lowestYOfBlock = positions[i].bottom;
      }
    }
    // the top of the element is below the overlap block, we are free.
    else {
      break;
    }
  }
  return end;
};

/**
 * Get blocks of overlapping elements. We only care if the content of the block overlaps, not the
 * margin. Margins can collapse together and they are not considered overlapping elements.
 */
export const detectOverlaps = (
  positions: ElementPosition[]
): OverlapBlock[] => {
  const blocks: OverlapBlock[] = [];

  positions.forEach((current, i) => {
    if (inOverlapBlock(i, blocks)) {
      // this has already been detected as part of an overlap block, keep going
      return;
    }
    const block = { start: i, end: i };

    // Compare the top and bottom of this element with the elements
    // above and below it. If they overlap (due to a CSS float), add
    // to the list of elements that have overlaps.

    // overlaps previous sibling
    if (positions[i - 1] && current.top < positions[i - 1].bottom) {
      block.start = findStart(i, positions);
    }
    // overlaps next sibling
    if (positions[i + 1] && current.bottom > positions[i + 1].top) {
      block.end = findEnd(i, positions);
    }

    if (block.start !== i || block.end !== i) {
      blocks.push(block);
    }
  });

  return blocks;
};
